import { MIN_IMAGE_LONG_SIDE, UPLOAD_LIMITS } from '@lite-workspace/uploader-core';
import { pluralRu } from '@lite-workspace/shared/format';
import type { IntakeRejectReason, IntakeSummary } from './photo-upload-coordinator.service';
import { bucketIn } from '@lite-workspace/shared/blocks/replace-confirm-dialog';

export interface IntakeSummaryMessage {
  /** Первая строка тоста — самое главное о дропе. */
  title: string;
  /** Вторая строка: всё остальное, что дроп должен сказать. Без неё тост однострочный. */
  detail?: string;
  tone: 'error' | 'info';
}

/** Plain reasons, in the order the toast lists them. The pixel minimum comes from the gate's own
 *  constant: a literal here would drift the moment the gate moves. Every one of them is a verdict on the
 *  FILE — 'deleting' is not (the same file goes in a moment later), so it gets its own line below. */
const REJECT_PHRASES: readonly (readonly [IntakeRejectReason, string])[] = [
  ['type', 'не изображения'],
  ['size', `размер файла может быть до ${UPLOAD_LIMITS.photoMaxBytes / 1e6} МБ`],
  ['resolution', `меньше ${MIN_IMAGE_LONG_SIDE} px по длинной стороне`],
  ['unverified', 'не сверили с проектом'],
];

/** Куда уехали перенесённые портреты — шаг называется так же, как в диалоге переноса. */
const MOVE_TARGET: Record<'student' | 'teacher', string> = { student: 'к ученикам', teacher: 'к педагогам' };

/** «Загружаем 35 фото» / «15 пропущено — уже есть в проекте»: одни и те же две строки говорят и «Оставить»
 *  в окне переноса, и «Загрузить новые» в окне замены — оба ответа оставляют файлы в проекте нетронутыми. */
function skipParts(queued: number, skipped: number): string[] {
  return queued > 0
    ? [`Загружаем ${queued} фото`, `${skipped} пропущено — уже есть в проекте`]
    : [`${skipped} фото пропущено`, 'уже есть в проекте'];
}

/** «35 новых загружаются» — свежие файлы дропа рядом с тем, что случилось с совпавшими. */
function freshLine(queued: number): string {
  const verb = pluralRu(queued, ['загружается', 'загружаются', 'загружаются']);
  return `${queued} ${pluralRu(queued, ['новое', 'новых', 'новых'])} ${verb}`;
}

/** The rewrite line of a ready project — the Photos page says it alone once the rewrites land. */
export function replacedTitle(count: number): string {
  return `Заменено ${count} фото`;
}

/** One toast per batch; a clean drop returns null — the grid itself is the confirmation. */
export function intakeSummaryMessage(summary: IntakeSummary): IntakeSummaryMessage | null {
  // The project cap refused the whole drop — nothing else happened to speak of.
  if (summary.rejected.some(item => item.reason === 'limit')) {
    return {
      title: `В проект можно загрузить до ${UPLOAD_LIMITS.fotoclassProjectMaxPhotos} фото`,
      tone: 'error',
    };
  }

  const parts: string[] = [];
  const moved = summary.moved;
  const step = summary.step;
  /** Свежие файлы дропа называет ОДНА строка, последняя: перенос и перезапись в одном дропе говорили
   *  о них каждый от себя, и «35 новых загружаются» читалось дважды (s2-ac8). */
  let fresh = false;
  // Мастер называет очередь во второй строке своих формулировок ниже — «N в очереди» повторило бы её.
  const stepSpeaksForQueue = step !== undefined && (summary.replaced > 0 || summary.skipped.length > 0);
  // A drop that answered the move window speaks for its own queue below — the fresh files are named
  // next to what happened to the moved ones, so «N в очереди» would say it a second time.
  if (summary.queued > 0 && moved === undefined && !stepSpeaksForQueue) parts.push(`${summary.queued} в очереди`);
  // A move rewrote nothing and uploaded nothing, so no other line can speak for it — it leads, and the
  // fresh files of a mixed drop follow it (s1-ac6.1).
  if (moved && moved.count > 0) {
    parts.push(
      `${moved.count} фото ${pluralRu(moved.count, ['перенесено', 'перенесены', 'перенесены'])} ${MOVE_TARGET[moved.to]}`,
    );
    // «Перенести и перезаписать»: the same rows are getting new bytes on top of the move (s1-ac1.3).
    if (moved.rewrite) parts.push('новые версии загружаются');
    if (summary.queued > 0) fresh = true;
  } else if (moved && moved.kept) {
    // «Оставить»: nothing moved and nothing uploaded under those names — they are skipped, and the
    // drop's own new files are named first (s1-ac1.2, s1-ac6.2). Совпавшие имена этого же дропа входят
    // в ту же сумму: двумя строками тост назвал бы пропущенные дважды и разными числами.
    parts.push(...skipParts(summary.queued, moved.kept + summary.skipped.length));
  }

  if (summary.replaced > 0) {
    if (step) {
      // Часть перезаписей легла в другие разделы: здесь их не увидеть, поэтому тост говорит о том, что
      // идёт сейчас, а результат покажет своё окно, когда пачка догрузится (s2-ac5).
      parts.push(step.away > 0 ? `Перезаписываем ${summary.replaced} фото` : `Перезаписано ${summary.replaced} фото`);
      // Раздел называется только тогда, когда о дропе больше сказать нечего — иначе он и так на экране.
      if (summary.queued > 0) fresh = true;
      else if (step.away === 0) parts.push(bucketIn(step.bucket, step.allInOne));
    } else parts.push(replacedTitle(summary.replaced));
  }

  // «Оставить» уже посчитало эти имена в своей сумме — второй раз их называть незачем.
  if (summary.skipped.length > 0 && !moved?.kept) {
    if (step === undefined)
      parts.push(`${summary.skipped.length} пропущено — ${summary.skipped.length === 1 ? 'имя' : 'имена'} уже в проекте`);
    else if (parts.length === 0) parts.push(...skipParts(summary.queued, summary.skipped.length));
    else parts.push(`${summary.skipped.length} пропущено — уже есть в проекте`);
  }

  const ambiguous = summary.skippedAmbiguous?.length ?? 0;
  if (ambiguous > 0)
    parts.push(`${ambiguous} пропущено — в проекте по несколько фото с ${ambiguous === 1 ? 'таким именем' : 'такими именами'}`);
  const skipped = summary.dropDuplicates.length;
  if (skipped > 0) parts.push(`${skipped} дубликатов пропущено`);
  // A deletion still on the wire says nothing about the file — it is held, not refused, and the very same
  // drop works a moment later. Mixing it into «не загрузили» (and into the red toast) called good files bad.
  const refused = summary.rejected.filter(item => item.reason !== 'deleting');
  const held = summary.rejected.length - refused.length;
  if (refused.length > 0) {
    const reasons = new Set(refused.map(item => item.reason));
    const phrases = REJECT_PHRASES.filter(([reason]) => reasons.has(reason)).map(([, phrase]) => phrase);
    parts.push(`${refused.length} не загрузили — ${phrases.join(', ')}`);
  }

  if (held > 0) parts.push(`${held} пока не загрузили — удаление ещё применяется, перетащите их ещё раз`);

  if (summary.unreadableDimensions.length > 0) parts.push(`${summary.unreadableDimensions.length} без проверки разрешения`);
  // Замыкает пояснение: что бы ни случилось с совпавшими именами, свежие файлы называются после этого.
  if (fresh) parts.push(freshLine(summary.queued));
  // An overwrite replaces a photo the person already had — it is never silent, even on an otherwise clean drop.
  // A drop that answered the move window is never a silent clean queue either: every answer decided
  // something about those files, and the lines above say what (s1-ac1.2, s1-ac4).
  if (
    moved === undefined &&
    summary.replaced === 0 &&
    skipped === 0 &&
    summary.skipped.length === 0 &&
    ambiguous === 0 &&
    summary.rejected.length === 0 &&
    summary.unreadableDimensions.length === 0
  )
    return null;
  if (parts.length === 0) return null;
  // Первая строка — заголовок, всё остальное — пояснение под ним.
  const [title, ...rest] = parts;
  return {
    title,
    ...(rest.length > 0 ? { detail: rest.join(' · ') } : {}),
    tone: refused.length > 0 ? 'error' : 'info',
  };
}
